import { useMemo } from 'react';

import type { QuestMissionTransition, QuestMissionTransitionType } from '../types';

const QUEST_MISSION_COMMAND_TYPES: Record<string, QuestMissionTransitionType> = {
  'RealWorld.Commands.GotoMission': 'goto'
};

function normalizeNumericIds(raw: unknown): number[] {
  if (Array.isArray(raw)) {
    return raw.flatMap((value) => normalizeNumericIds(value));
  }

  if (typeof raw === 'object' && raw !== null) {
    if ('value' in raw) {
      return normalizeNumericIds((raw as { value?: unknown }).value);
    }
    return [];
  }

  if (typeof raw === 'string') {
    return raw
      .split(',')
      .map((value) => Number(value.trim()))
      .filter((value) => Number.isFinite(value));
  }

  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return [raw];
  }

  return [];
}

interface AddQuestMissionTransitionsParams {
  commands: ReactionCommand[] | undefined;
  quest: Quest;
  missionId: number;
  missionIds: Set<number>;
  dependencies: Map<string, QuestMissionTransition>;
}

function addQuestMissionTransitions({
  commands,
  quest,
  missionId,
  missionIds,
  dependencies
}: AddQuestMissionTransitionsParams) {
  if (!commands) return;

  for (const command of commands) {
    const edgeType = QUEST_MISSION_COMMAND_TYPES[command.type];
    if (edgeType) {
      const targetMissionIds = normalizeNumericIds(command.parameters?.missionId);
      for (const targetMissionId of targetMissionIds) {
        if (missionIds.has(targetMissionId)) {
          const key = `${quest.id}-${targetMissionId}-${edgeType}`;
          dependencies.set(key, {
            sourceQuestId: quest.id,
            sourceMissionId: missionId,
            targetMissionId,
            edgeType
          });
        }
      }
    }
  }
}

function parseQuestMissionTransitions(missions: Mission[]): QuestMissionTransition[] {
  const dependencies = new Map<string, QuestMissionTransition>();
  const missionIds = new Set(missions.map((mission) => mission.id));

  for (const mission of missions) {
    if (mission.quests) {
      for (const quest of mission.quests) {
        const { actionHandler } = quest;
        if (actionHandler) {
          for (const reaction of actionHandler.reactionDefinitions ?? []) {
            addQuestMissionTransitions({
              commands: reaction.commands,
              quest,
              missionId: mission.id,
              missionIds,
              dependencies
            });
          }

          addQuestMissionTransitions({
            commands: actionHandler.failureCommands,
            quest,
            missionId: mission.id,
            missionIds,
            dependencies
          });
        }
      }
    }
  }

  return [...dependencies.values()];
}

export default function useQuestMissionTransitions(
  missions: Mission[] | undefined
): QuestMissionTransition[] {
  return useMemo(() => {
    if (!missions) return [];
    return parseQuestMissionTransitions(missions);
  }, [missions]);
}
